// docs/116 CI7: the bard has the music bus while my band is in the inn (and, softer, through the floor
// upstairs). The tune is `song.ts currentSong`: everyone in the room hears the same one from the same
// second, so a player who walks in mid-tune joins it where the room is (`startAt`). Between tunes the
// bus is quiet but for the room's own sound. What plays is told to the room (`useInn.playing`) so the
// guests clap along and the room's ambience sinks under it.
import { useEffect, useRef } from 'react';
import { playMusic, stopMusic } from '../../../../core/audio';
import { useMusicHold } from '../../../../core/audio/music';
import { serverNow } from '../../../../core/time';
import { secondsInto, useCurrentSong } from './song';
import { useInn } from './store';
import type { InnData } from './types';
import type { VillageLook } from '../types';

const FADE_IN_MS = 1800;
const FADE_OUT_MS = 2500;
/** Upstairs the tune is the inn heard through the floorboards. */
const UPSTAIRS_GAIN = 0.35;

export function BardDirector({ data, look, upstairs }: { data: InnData; look: VillageLook; upstairs: boolean }): null {
  const song = useCurrentSong(data, look);
  const was = useRef<string | undefined>(undefined);
  const gain = upstairs ? UPSTAIRS_GAIN : 1;

  useEffect(() => {
    const key = song ? `${song.tune}@${song.startedAtMs}` : undefined;
    if (!song) {
      if (was.current) stopMusic(FADE_OUT_MS);
      was.current = undefined;
      useInn.setState({ playing: undefined });
      return;
    }
    const startAt = secondsInto(song, serverNow());
    // a fresh tune (or the same one heard at a new level) starts at the room's moment in it
    playMusic('music.tavern', { variant: song.tune, gain, fadeMs: was.current === key ? 900 : startAt > 1 ? FADE_IN_MS : 400, ...(startAt > 0.25 ? { startAt } : {}) });
    was.current = key;
    useInn.setState({ playing: { tune: song.tune, startedAtMs: song.startedAtMs, by: song.by } });
  }, [song, gain]);

  // leaving the inn: the bard's tune fades and the score has the bus again
  useEffect(() => () => {
    if (was.current) stopMusic(FADE_OUT_MS);
    was.current = undefined;
    useInn.setState({ playing: undefined });
  }, []);
  useMusicHold(true);
  return null;
}
